import { api } from "../../../api/Middleware";
import {
  API_URL,
  PUBLICATIONS_URL,
  MARKETPLACE_CATEGORIES_URL,
  MARKETPLACE_AGENTS_URL,
  MARKETPLACE_API_URL,
} from "../../../api/Urls";

// =============================
// MARKETPLACE SERVICES
// =============================

class MarketplaceServices {
  // =============================
  // CRUD OPERATIONS
  // =============================

  // Listar todas as publicações do usuário
  static async listPublications(params = {}) {
    try {
      const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
        
      const url = queryString ? `${PUBLICATIONS_URL}?${queryString}` : PUBLICATIONS_URL;
      return await api.get(url);
    } catch (error) {
      console.error('Erro ao buscar publicações do marketplace:', error);
      throw error;
    }
  }

  // Buscar publicação específica
  static async getPublication(id) {
    console.log(id);
    try {
      return await api.get(`${PUBLICATIONS_URL}${id}/`);
    } catch (error) {
      console.error('Erro ao buscar publicação do marketplace:', error);
      throw error;
    }
  }

  // Atualizar publicação existente (PUT)
  static async updatePublication(id, formData) {
    try {
      const updateUrl = `${PUBLICATIONS_URL}${id}/`;
      
      // Criar FormData para multipart/form-data (mesmo formato do publishAgent)
      const multipartData = new FormData();
      
      console.log('🔄 Atualizando publicação ID:', id, {
        agent_id: formData.agent_id,
        agent_data_keys: formData.agent_data ? Object.keys(formData.agent_data) : [],
        has_banner: !!formData.banner,
        gallery_count: formData.gallery_images ? formData.gallery_images.length : 0
      });

      // Reutilizar lógica similar ao publishAgent
      // === CAMPOS OBRIGATÓRIOS ===
      
      // agent_id (obrigatório - ID do agente na plataforma)
      if (formData.agent_id) {
        multipartData.append('agent_id', formData.agent_id.toString());
        console.log('✅ agent_id:', formData.agent_id);
      } else {
        throw new Error('agent_id é obrigatório');
      }
      
      // Campos individuais do agent_data (NÃO como nested object)
      if (formData.agent_data) {
        const agentData = formData.agent_data;
        
        // agent_name (obrigatório - campo correto do model)
        if (agentData.name) {
          multipartData.append('agent_name', agentData.name);
          console.log('✅ agent_name:', agentData.name);
        }
        
        // agent_description (campo separado para descrição básica)
        if (agentData.description) {
          multipartData.append('agent_description', agentData.description);
          console.log('✅ agent_description:', agentData.description.substring(0, 50) + '...');
        }
        
        // agent_job (obrigatório - campo correto do model)
        if (agentData.job) {
          multipartData.append('agent_job', agentData.job);
          console.log('✅ agent_job:', agentData.job);
        }
        
        // price (obrigatório - decimal field)
        if (agentData.price) {
          multipartData.append('price', agentData.price.toString());
          console.log('✅ price:', agentData.price);
        }
        
        // video_url (nome correto no model)
        if (agentData.demo_url || agentData.video_url) {
          const videoUrl = agentData.video_url || agentData.demo_url;
          multipartData.append('video_url', videoUrl);
          console.log('✅ video_url:', videoUrl);
        }
        
        // playground_url (novo campo para URL do playground de testes)
        if (agentData.playground_url) {
          multipartData.append('playground_url', agentData.playground_url);
          console.log('✅ playground_url:', agentData.playground_url);
        }
        
        // === ARRAYS COMO JSON STRINGS ===
        
        // categories (single category ID - cliente seleciona apenas 1 categoria)
        if (agentData.category_ids) {
          let categoryId;
          if (Array.isArray(agentData.category_ids) && agentData.category_ids.length > 0) {
            // Se for array, pega o primeiro
            categoryId = agentData.category_ids[0];
          } else if (typeof agentData.category_ids === 'number' || typeof agentData.category_ids === 'string') {
            // Se for número ou string, usa diretamente
            categoryId = agentData.category_ids;
          }
          
          if (categoryId) {
            multipartData.append('categories', categoryId.toString());
            console.log('✅ categories (single ID):', categoryId);
          }
        }
        
        // tags
        if (agentData.tags && Array.isArray(agentData.tags)) {
          const tagsJson = JSON.stringify(agentData.tags);
          multipartData.append('tags', tagsJson);
          console.log('✅ tags (JSON):', tagsJson);
        }
        
        // features
        if (agentData.features && Array.isArray(agentData.features)) {
          const featuresJson = JSON.stringify(agentData.features);
          multipartData.append('features', featuresJson);
          console.log('✅ features (JSON):', featuresJson);
        }
        
        // capabilities
        if (agentData.capabilities && Array.isArray(agentData.capabilities)) {
          const capabilitiesJson = JSON.stringify(agentData.capabilities);
          multipartData.append('capabilities', capabilitiesJson);
          console.log('✅ capabilities (JSON):', capabilitiesJson);
        }
        
        // platform
        if (agentData.platform && Array.isArray(agentData.platform)) {
          const platformJson = JSON.stringify(agentData.platform);
          multipartData.append('platform', platformJson);
          console.log('✅ platform (JSON):', platformJson);
        }
        
        // === OBJECTS COMO JSON STRINGS ===
        
        // instructions
        if (agentData.instructions && typeof agentData.instructions === 'object') {
          const instructionsJson = JSON.stringify(agentData.instructions);
          multipartData.append('instructions', instructionsJson);
          console.log('✅ instructions (JSON):', instructionsJson.substring(0, 100) + '...');
        }
        
        // pricing_info
        if (agentData.pricing_info && typeof agentData.pricing_info === 'object') {
          const pricingInfoJson = JSON.stringify(agentData.pricing_info);
          multipartData.append('pricing_info', pricingInfoJson);
          console.log('✅ pricing_info (JSON):', pricingInfoJson);
        }
        
        // technical_specs
        if (agentData.technical_specs) {
          const techSpecsValue = typeof agentData.technical_specs === 'string' 
            ? agentData.technical_specs 
            : JSON.stringify(agentData.technical_specs);
          multipartData.append('technical_specs', techSpecsValue);
          console.log('✅ technical_specs:', techSpecsValue.substring(0, 100) + '...');
        }
        
        // description (campo de marketing rico - TextField no model)
        if (agentData.rich_description) {
          multipartData.append('description', agentData.rich_description);
          console.log('✅ description (rich):', agentData.rich_description.substring(0, 50) + '...');
        }
        
        // for_download (campo específico da API)
        if (agentData.for_download !== undefined) {
          multipartData.append('for_download', agentData.for_download.toString());
          console.log('✅ for_download:', agentData.for_download);
        } else {
          // Default para false
          multipartData.append('for_download', 'false');
          console.log('✅ for_download (default):', false);
        }
      }
      
      // === ARQUIVOS ===
      
      // Banner principal
      if (formData.banner) {
        multipartData.append('banner', formData.banner, 'banner.jpg');
        console.log('✅ banner file attached');
      }
      
      // Galeria de imagens
      if (formData.gallery_images && Array.isArray(formData.gallery_images)) {
        formData.gallery_images.forEach((file, index) => {
          if (file instanceof File || file instanceof Blob) {
            multipartData.append('gallery_images', file, `gallery_${index}.jpg`);
          }
        });
        console.log(`✅ ${formData.gallery_images.length} gallery images attached`);
      }

      const response = await api.put(updateUrl, multipartData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('🔄 Resposta da atualização:', {
        success: response.data.success,
        status: response.status,
        has_data: !!response.data.data,
        message: response.data.message
      });
      
      return response;
    } catch (error) {
      console.error('Erro ao atualizar publicação do marketplace:', error);
      throw error;
    }
  }


  // =============================
  // STATUS MANAGEMENT
  // =============================

  // Publicar agente no marketplace (criar nova publicação)
  static async publishAgent(formData) {
    try {
      const publishUrl = `${PUBLICATIONS_URL}`;
      
      // Criar FormData para multipart/form-data
      const multipartData = new FormData();
      
      console.log('📦 Criando nova publicação:', {
        agent_id: formData.agent_id,
        agent_data_keys: formData.agent_data ? Object.keys(formData.agent_data) : [],
        has_banner: !!formData.banner,
        gallery_count: formData.gallery_images ? formData.gallery_images.length : 0
      });

      // === CAMPOS OBRIGATÓRIOS ===
      
      // agent_id (obrigatório - ID do agente na plataforma)
      if (formData.agent_id) {
        multipartData.append('agent_id', formData.agent_id.toString());
        console.log('✅ agent_id:', formData.agent_id);
      } else {
        throw new Error('agent_id é obrigatório');
      }
      
      // NOTA: Os seguintes campos são preenchidos automaticamente pelo backend:
      // - agent_slug: gerado automaticamente a partir do agent_name
      // - seller_profile: obtido do usuário autenticado
      // - marketplace_product_id: gerado automaticamente (AGENT-{11 dígitos})
      // - marketplace_url: gerado automaticamente
      // - status: definido como 'pending' inicialmente
      // - company_name: obtido da organização do usuário
      // - company_logo: obtido da organização do usuário
      // - created_at/updated_at: timestamps automáticos
      
      // Campos individuais do agent_data (NÃO como nested object)
      if (formData.agent_data) {
        const agentData = formData.agent_data;
        
        // agent_name (obrigatório - campo correto do model)
        if (agentData.name) {
          multipartData.append('agent_name', agentData.name);
          console.log('✅ agent_name:', agentData.name);
        }
        
        // agent_description (campo separado para descrição básica)
        if (agentData.description) {
          multipartData.append('agent_description', agentData.description);
          console.log('✅ agent_description:', agentData.description.substring(0, 50) + '...');
        }
        
        // agent_job (obrigatório - campo correto do model)
        if (agentData.job) {
          multipartData.append('agent_job', agentData.job);
          console.log('✅ agent_job:', agentData.job);
        }
        
        // price (obrigatório - decimal field)
        if (agentData.price) {
          multipartData.append('price', agentData.price.toString());
          console.log('✅ price:', agentData.price);
        }
        
        // video_url (nome correto no model)
        if (agentData.demo_url || agentData.video_url) {
          const videoUrl = agentData.video_url || agentData.demo_url;
          multipartData.append('video_url', videoUrl);
          console.log('✅ video_url:', videoUrl);
        }
        
        // playground_url (novo campo para URL do playground de testes)
        if (agentData.playground_url) {
          multipartData.append('playground_url', agentData.playground_url);
          console.log('✅ playground_url:', agentData.playground_url);
        }
        
        // === ARRAYS COMO JSON STRINGS ===
        
        // categories (single category ID - cliente seleciona apenas 1 categoria)
        if (agentData.category_ids) {
          let categoryId;
          if (Array.isArray(agentData.category_ids) && agentData.category_ids.length > 0) {
            // Se for array, pega o primeiro
            categoryId = agentData.category_ids[0];
          } else if (typeof agentData.category_ids === 'number' || typeof agentData.category_ids === 'string') {
            // Se for número ou string, usa diretamente
            categoryId = agentData.category_ids;
          }
          
          if (categoryId) {
            multipartData.append('categories', categoryId.toString());
            console.log('✅ categories (single ID):', categoryId);
          }
        }
        
        // tags
        if (agentData.tags && Array.isArray(agentData.tags)) {
          const tagsJson = JSON.stringify(agentData.tags);
          multipartData.append('tags', tagsJson);
          console.log('✅ tags (JSON):', tagsJson);
        }
        
        // features
        if (agentData.features && Array.isArray(agentData.features)) {
          const featuresJson = JSON.stringify(agentData.features);
          multipartData.append('features', featuresJson);
          console.log('✅ features (JSON):', featuresJson);
        }
        
        // capabilities
        if (agentData.capabilities && Array.isArray(agentData.capabilities)) {
          const capabilitiesJson = JSON.stringify(agentData.capabilities);
          multipartData.append('capabilities', capabilitiesJson);
          console.log('✅ capabilities (JSON):', capabilitiesJson);
        }
        
        // platform
        if (agentData.platform && Array.isArray(agentData.platform)) {
          const platformJson = JSON.stringify(agentData.platform);
          multipartData.append('platform', platformJson);
          console.log('✅ platform (JSON):', platformJson);
        }
        
        // === OBJECTS COMO JSON STRINGS ===
        
        // instructions
        if (agentData.instructions && typeof agentData.instructions === 'object') {
          const instructionsJson = JSON.stringify(agentData.instructions);
          multipartData.append('instructions', instructionsJson);
          console.log('✅ instructions (JSON):', instructionsJson.substring(0, 100) + '...');
        }
        
        // pricing_info
        if (agentData.pricing_info && typeof agentData.pricing_info === 'object') {
          const pricingInfoJson = JSON.stringify(agentData.pricing_info);
          multipartData.append('pricing_info', pricingInfoJson);
          console.log('✅ pricing_info (JSON):', pricingInfoJson);
        }
        
        // technical_specs
        if (agentData.technical_specs) {
          const techSpecsValue = typeof agentData.technical_specs === 'string' 
            ? agentData.technical_specs 
            : JSON.stringify(agentData.technical_specs);
          multipartData.append('technical_specs', techSpecsValue);
          console.log('✅ technical_specs:', techSpecsValue.substring(0, 100) + '...');
        }
        
        // description (campo de marketing rico - TextField no model)
        if (agentData.rich_description) {
          multipartData.append('description', agentData.rich_description);
          console.log('✅ description (rich):', agentData.rich_description.substring(0, 50) + '...');
        }
        
        // === CONFIGURAÇÕES ===
        
        // for_download (BooleanField no model)
        multipartData.append('for_download', (agentData.for_download || false).toString());
        console.log('✅ for_download:', agentData.for_download || false);
      }
      
      // === ARQUIVOS ===
      
      // banner
      if (formData.banner && formData.banner instanceof File) {
        multipartData.append('banner', formData.banner, formData.banner.name);
        console.log('✅ banner:', formData.banner.name, formData.banner.size, 'bytes');
      }
      
      // gallery_images (ImageField files - upload individual)
      if (formData.gallery_images && Array.isArray(formData.gallery_images)) {
        let addedImages = 0;
        formData.gallery_images.forEach((image, index) => {
          if (image instanceof File) {
            multipartData.append(`gallery_image_${index}`, image, image.name);
            addedImages++;
            console.log(`✅ gallery_image_${index}:`, image.name, image.size, 'bytes');
          }
        });
        console.log(`🖼️ Total gallery images: ${addedImages}`);
      }
      
      // === RESUMO FINAL ===
      console.log('🏁 Enviando requisição para:', publishUrl);
      console.log('📎 Estrutura correta da API - Campos individuais no form-data:');
      
      // Contar e mostrar estrutura final
      let fieldCount = 0;
      let fileCount = 0;
      let jsonFieldCount = 0;
      
      for (let [key, value] of multipartData.entries()) {
        fieldCount++;
        if (value instanceof File) {
          fileCount++;
          console.log(`  📁 ${key}: [File] ${value.name} (${value.size} bytes, ${value.type})`);
        } else {
          // Detectar se é JSON
          let displayValue = value;
          if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
            jsonFieldCount++;
            displayValue = `[JSON] ${value.substring(0, 50)}${value.length > 50 ? '...' : ''}`;
          } else if (typeof value === 'string' && value.length > 50) {
            displayValue = value.substring(0, 50) + '...';
          }
          console.log(`  📝 ${key}: ${displayValue}`);
        }
      }
      
      console.log(`📈 Resumo: ${fieldCount} campos total (${fieldCount - fileCount - jsonFieldCount} simples, ${jsonFieldCount} JSON, ${fileCount} arquivos)`);
      
      const response = await api.post(publishUrl, multipartData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('🚀 Resposta da API:', {
        success: response.data.success,
        status: response.status,
        has_data: !!response.data.data,
        message: response.data.message
      });
      
      return response;
    } catch (error) {
      console.error('Erro ao publicar agente no marketplace:', error);
      throw error;
    }
  }

  // Listar agentes publicados da conta
  static async listPublishedAgents(params = {}) {
    try {
      const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
        
      const url = `${PUBLICATIONS_URL}`;
      const fullUrl = queryString ? `${url}?${queryString}` : url;
      return await api.get(fullUrl);
    } catch (error) {
      console.error('Erro ao buscar agentes publicados:', error);
      throw error;
    }
  }

  // Solicitar publicação (muda status para pending) - endpoint legado
  static async publishPublication(id) {
    try {
      return await api.post(`${PUBLICATIONS_URL}${id}/publish/`);
    } catch (error) {
      console.error('Erro ao solicitar publicação:', error);
      throw error;
    }
  }

  // Suspender publicação (usuário pode suspender temporariamente)
  static async suspendPublication(id) {
    try {
      return await api.post(`${PUBLICATIONS_URL}${id}/suspend/`);
    } catch (error) {
      console.error('Erro ao suspender publicação:', error);
      throw error;
    }
  }

  // Remover publicação permanentemente
  static async removePublication(id) {
    try {
      return await api.post(`${PUBLICATIONS_URL}${id}/remove/`);
    } catch (error) {
      console.error('Erro ao remover publicação:', error);
      throw error;
    }
  }

  // Obter resumo de status das publicações do usuário
  static async getStatusSummary() {
    try {
      return await api.get(`${PUBLICATIONS_URL}status_summary/`);
    } catch (error) {
      console.error('Erro ao buscar resumo de status:', error);
      throw error;
    }
  }

  // =============================
  // SUPPORT ENDPOINTS
  // =============================

  // Listar categorias disponíveis
  static async getCategories(params = {}) {
    try {
      const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
        
      const url = queryString
        ? `${MARKETPLACE_API_URL}categories/?${queryString}`
        : `${MARKETPLACE_API_URL}categories/`;
      return await api.get(url);
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
      throw error;
    }
  }

  // Buscar árvore completa de categorias
  static async getCategoriesTree() {
    try {
      return await api.get(`${MARKETPLACE_CATEGORIES_URL}tree/`);
    } catch (error) {
      console.error('Erro ao buscar árvore de categorias:', error);
      throw error;
    }
  }

  // Listar agentes elegíveis para publicação
  static async getEligibleAgents(params = {}) {
    try {
      const queryString = Object.keys(params)
        .filter(key => params[key] !== undefined && params[key] !== null)
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
        
      const url = queryString ? `${MARKETPLACE_AGENTS_URL}?${queryString}` : MARKETPLACE_AGENTS_URL;
      return await api.get(url);
    } catch (error) {
      console.error('Erro ao buscar agentes elegíveis:', error);
      throw error;
    }
  }

  // =============================
  // HELPER FUNCTIONS
  // =============================

  // Função auxiliar para construir parâmetros de busca
  static buildSearchParams(filters = {}) {
    const params = {};
    
    // Filtros básicos
    if (filters.status && filters.status !== 'all') {
      params.status = filters.status;
    }
    
    if (filters.search && filters.search.trim()) {
      params.search = filters.search.trim();
    }
    
    // Paginação
    if (filters.page) {
      params.page = filters.page;
    }
    
    if (filters.page_size) {
      params.page_size = filters.page_size;
    }
    
    // Filtros específicos para marketplace
    if (filters.unpublished_only) {
      params.unpublished_only = filters.unpublished_only;
    }
    
    return params;
  }

  // Função auxiliar para formatar dados da publicação
  static formatPublicationData(publication) {
    if (!publication) return null;

    // Mapear os campos da nova estrutura da API
    return {
      ...publication,
      // Mapear campos principais
      id: publication.id || publication.marketplace_product_id,
      name: publication.agent_name || publication.name,
      area: publication.agent_area || publication.area,
      price: publication.pricing_info?.price || publication.price || 0,
      
      // Informações do proprietário
      owner_name: publication.owner_info?.company_name || publication.owner_name,
      owner_rating: publication.owner_info?.rating || 0,
      owner_sales: publication.owner_info?.total_sales || 0,
      owner_response_time: publication.owner_info?.response_time || '24h',
      owner_member_since: publication.owner_info?.member_since,
      owner_verified: publication.owner_info?.verified || false,
      owner_top_seller: publication.owner_info?.top_seller || false,
      
      // Informações de preço
      pricing_info: {
        price: publication.pricing_info?.price || publication.price || 0,
        currency: publication.pricing_info?.currency || 'BRL',
        billing_type: publication.pricing_info?.billing_type || 'one_time',
        ...publication.pricing_info
      },
      
      // Campos de data formatados
      formattedCreatedAt: publication.created_at 
        ? new Date(publication.created_at).toLocaleDateString('pt-BR')
        : '',
      formattedUpdatedAt: publication.updated_at
        ? new Date(publication.updated_at).toLocaleDateString('pt-BR')
        : '',
      formattedPublishedAt: publication.published_at
        ? new Date(publication.published_at).toLocaleDateString('pt-BR')
        : null,
      
      // Status e permissões
      statusVariant: MarketplaceServices.getStatusVariant(publication.status),
      statusText: MarketplaceServices.getStatusText(publication.status),
      canEdit: MarketplaceServices.canEdit(publication.status),
      canPublish: MarketplaceServices.canPublish(publication.status),
      canSuspend: MarketplaceServices.canSuspend(publication.status),
      canRemove: MarketplaceServices.canRemove(publication.status),
      canReactivate: MarketplaceServices.canReactivate(publication.status),
    };
  }

  // Função auxiliar para obter variante de cor do status
  static getStatusVariant(status) {
    const variants = {
      published: "success",
      pending: "warning", 
      suspended: "secondary",
      removed: "danger",
      rejected: "danger"
    };
    return variants[status] || "secondary";
  }

  // Função auxiliar para obter texto do status
  static getStatusText(status) {
    const texts = {
      published: "Publicado",
      pending: "Aguardando Aprovação",
      suspended: "Suspenso", 
      removed: "Removido",
      rejected: "Rejeitado"
    };
    return texts[status] || status;
  }

  // Verificações de permissão para ações
  static canEdit(status) {
    return !['removed'].includes(status);
  }

  static canPublish(status) {
    return ['rejected', 'suspended'].includes(status);
  }

  static canSuspend(status) {
    return ['published'].includes(status);
  }

  static canRemove(status) {
    return ['published', 'suspended', 'pending', 'rejected'].includes(status);
  }

  static canReactivate(status) {
    return ['suspended'].includes(status);
  }

  // Validar dados para criação de publicação conforme API
  static validatePublicationData(data) {
    const errors = [];
    
    // Campo obrigatório: agent_id
    const agentId = data.agent_id || data.agent?.id || data.agent;
    if (!agentId) {
      errors.push("Agente é obrigatório");
    }

    // Validar nome (obrigatório)
    if (!data.agent_name && !data.name) {
      errors.push("Nome do agente é obrigatório");
    } else {
      const name = data.agent_name || data.name;
      if (name && name.trim().length < 3) {
        errors.push("Nome do agente deve ter pelo menos 3 caracteres");
      }
    }

    // Validar função/job (obrigatório conforme API)
    if (!data.agent_job && !data.job) {
      errors.push("Função do agente é obrigatória");
    }

    // Validar preço - sempre obrigatório e mínimo R$ 250,00 conforme API
    if (!data.price || data.price === '') {
      errors.push("Preço é obrigatório");
    } else {
      const price = parseFloat(data.price);
      if (isNaN(price)) {
        errors.push("Preço deve ser um número válido");
      } else if (price < 250) {
        errors.push("Preço mínimo é R$ 250,00 conforme API");
      }
    }

    // Validar banner se fornecido
    if (data.banner) {
      const bannerFile = data.banner instanceof File ? data.banner : data.banner.file;
      if (bannerFile && bannerFile instanceof File) {
        const bannerValidation = MarketplaceServices.validateImageFile(bannerFile, 3);
        if (!bannerValidation.isValid) {
          errors.push(`Banner: ${bannerValidation.errors.join(', ')}`);
        }
      }
    }

    // Validar galeria se fornecida
    if (data.gallery_images && Array.isArray(data.gallery_images)) {
      const galleryFiles = data.gallery_images
        .map(img => img instanceof File ? img : img.file)
        .filter(file => file instanceof File);
      
      if (galleryFiles.length > 0) {
        const galleryValidation = MarketplaceServices.validateGalleryFiles(galleryFiles, 10, 3);
        if (!galleryValidation.isValid) {
          errors.push(`Galeria: ${galleryValidation.errors.join(', ')}`);
        }
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }


  // Preparar dados para publicação de agente - Estrutura correta da API
  static prepareAgentPublishData(formData, agentId) {
    // Extrair ID do agente se foi passado um objeto ou um ID direto
    const finalAgentId = agentId || (formData.agent?.id || formData.agent);
    
    if (!finalAgentId) {
      throw new Error('agent_id é obrigatório para publicação');
    }
    
    console.log('📋 Preparando dados do formulário:', {
      agent_id: finalAgentId,
      agent_name: formData.agent_name,
      agent_job: formData.agent_job,
      price: formData.price,
      categories: formData.categories,
      has_banner: !!formData.banner,
      gallery_count: formData.gallery_images ? formData.gallery_images.length : 0
    });
    
    const publishData = {
      agent_id: parseInt(finalAgentId),
      agent_data: {},
      banner: null,
      gallery_images: []
    };

    // === DADOS BÁSICOS (obrigatórios) ===
    // Nome do agente
    if (formData.agent_name) {
      publishData.agent_data.name = formData.agent_name.trim();
    } else if (formData.name) {
      publishData.agent_data.name = formData.name.trim();
    }

    // Descrição
    if (formData.description) {
      publishData.agent_data.description = formData.description.trim();
    }

    // Função/Cargo
    if (formData.agent_job) {
      publishData.agent_data.job = formData.agent_job.trim();
    } else if (formData.job) {
      publishData.agent_data.job = formData.job.trim();
    }

    // Preço mínimo R$ 250,00 conforme documentação
    if (formData.price) {
      const price = parseFloat(formData.price);
      if (price >= 250) {
        publishData.agent_data.price = price;
      }
    }

    // === CONTEÚDO RICO ===
    if (formData.detailed_content?.overview || formData.rich_description) {
      publishData.agent_data.rich_description = formData.detailed_content?.overview || formData.rich_description || '';
    }

    if (formData.video_url || formData.demo_url) {
      publishData.agent_data.demo_url = (formData.video_url || formData.demo_url || '').trim();
    }

    // URL do playground de testes
    if (formData.playground_url) {
      publishData.agent_data.playground_url = formData.playground_url.trim();
    }

    if (formData.detailed_content?.technical_specs || formData.technical_specs) {
      const techSpecs = formData.detailed_content?.technical_specs || formData.technical_specs;
      publishData.agent_data.technical_specs = typeof techSpecs === 'string' ? techSpecs : JSON.stringify(techSpecs);
    }

    // === CATEGORIZAÇÃO ===
    if (formData.categories && formData.categories.length > 0) {
      publishData.agent_data.category_ids = formData.categories.map(cat => typeof cat === 'object' ? cat.id : cat);
    }

    if (formData.tags && formData.tags.length > 0) {
      publishData.agent_data.tags = formData.tags;
    }

    // === FUNCIONALIDADES ===
    if (formData.features && formData.features.length > 0) {
      publishData.agent_data.features = formData.features;
    }

    if (formData.capabilities && formData.capabilities.length > 0) {
      publishData.agent_data.capabilities = formData.capabilities;
    }

    if (formData.platform && formData.platform.length > 0) {
      publishData.agent_data.platform = formData.platform;
    }

    // === INSTRUÇÕES ESTRUTURADAS ===
    const instructions = {};
    
    // Platform Access
    if (formData.platform_name || formData.platform_url || formData.platform_description) {
      instructions.platform_access = {
        platform_name: formData.platform_name || '',
        platform_url: formData.platform_url || '',
        platform_description: formData.platform_description || ''
      };
    }
    
    // Access Credentials
    if (formData.credential_type || formData.access_instructions || formData.additional_info) {
      instructions.access_credentials = {
        credential_type: formData.credential_type || '',
        instructions: formData.access_instructions || '',
        additional_info: formData.additional_info || ''
      };
    }
    
    // Agent Configuration
    if (formData.configuration_steps || formData.customization_options || formData.setup_requirements) {
      instructions.agent_configuration = {
        configuration_steps: formData.configuration_steps || '',
        customization_options: formData.customization_options || '',
        setup_requirements: formData.setup_requirements || ''
      };
    }
    
    // Tutorial Video
    if (formData.video_tutorial_url || formData.video_platform || formData.video_description) {
      instructions.tutorial_video = {
        video_url: formData.video_tutorial_url || '',
        video_platform: formData.video_platform || '',
        video_description: formData.video_description || ''
      };
    }
    
    // Documentation
    if (formData.document_url || formData.document_type || formData.document_description) {
      instructions.documentation = {
        document_url: formData.document_url || '',
        document_type: formData.document_type || '',
        document_description: formData.document_description || ''
      };
    }
    
    // Support Contact
    if (formData.contact_type || formData.contact_value || formData.contact_name || formData.support_hours) {
      instructions.support_contact = {
        contact_type: formData.contact_type || '',
        contact_value: formData.contact_value || '',
        contact_name: formData.contact_name || '',
        support_hours: formData.support_hours || ''
      };
    }
    
    if (Object.keys(instructions).length > 0) {
      publishData.agent_data.instructions = instructions;
    }

    // === INFORMAÇÕES DE PREÇO ===
    const pricingInfo = {
      price: parseFloat(formData.price) || 250.00,
      currency: 'BRL',
      billing_type: 'one_time'
    };
    
    if (formData.pricing_info?.original_price) {
      pricingInfo.original_price = parseFloat(formData.pricing_info.original_price);
    }
    
    if (formData.pricing_info?.discount_percentage) {
      pricingInfo.discount_percentage = parseFloat(formData.pricing_info.discount_percentage);
    }
    
    if (formData.pricing_info?.trial_period_days) {
      pricingInfo.trial_period_days = parseInt(formData.pricing_info.trial_period_days);
    }
    
    publishData.agent_data.pricing_info = pricingInfo;

    // === CONFIGURAÇÕES AVANÇADAS ===
    publishData.agent_data.for_download = formData.for_download || false;
    publishData.agent_data.auto_publish = formData.auto_publish || true;

    // === ARQUIVOS ===
    // Banner
    if (formData.banner) {
      if (formData.banner instanceof File) {
        publishData.banner = formData.banner;
      } else if (formData.banner.file instanceof File) {
        publishData.banner = formData.banner.file;
      }
    }

    // Gallery Images
    if (formData.gallery_images && Array.isArray(formData.gallery_images)) {
      publishData.gallery_images = formData.gallery_images
        .filter(image => image instanceof File || (image.file instanceof File))
        .map(image => image instanceof File ? image : image.file)
        .slice(0, 10); // Máximo 10 imagens conforme API
    }

    return publishData;
  }

  // Função para obter estatísticas das publicações
  static async getPublicationStats() {
    try {
      const [summaryResponse, publicationsResponse] = await Promise.all([
        MarketplaceServices.getStatusSummary(),
        MarketplaceServices.listPublications({ page_size: 1 })
      ]);
      
      const summary = summaryResponse.data;
      const total = publicationsResponse.data.count || 0;
      
      // Calcular percentuais
      const percentages = {};
      Object.keys(summary).forEach(status => {
        if (status !== 'total' && total > 0) {
          percentages[status] = Math.round((summary[status] / total) * 100);
        } else if (status !== 'total') {
          percentages[status] = 0;
        }
      });
      
      return {
        summary,
        total,
        percentages,
      };
    } catch (error) {
      console.error('Erro ao obter estatísticas:', error);
      throw error;
    }
  }


  // Função para buscar publicações com filtros avançados
  static async searchPublications(searchOptions = {}) {
    try {
      const {
        query = '',
        status = 'all',
        dateFrom = null,
        dateTo = null,
        sortBy = 'created_at',
        sortOrder = 'desc',
        page = 1,
        pageSize = 10
      } = searchOptions;

      const params = {
        page,
        page_size: pageSize,
      };

      if (query.trim()) {
        params.search = query.trim();
      }

      if (status && status !== 'all') {
        params.status = status;
      }

      if (dateFrom) {
        params.created_after = dateFrom;
      }

      if (dateTo) {
        params.created_before = dateTo;
      }

      if (sortBy) {
        params.ordering = sortOrder === 'desc' ? `-${sortBy}` : sortBy;
      }

      return await MarketplaceServices.listPublications(params);
    } catch (error) {
      console.error('Erro na busca avançada:', error);
      throw error;
    }
  }

  // Função para exportar dados das publicações
  static async exportPublications(format = 'json', filters = {}) {
    try {
      const params = {
        ...MarketplaceServices.buildSearchParams(filters),
        export_format: format,
        page_size: 1000, // Limite alto para exportação
      };

      const response = await MarketplaceServices.listPublications(params);
      const data = response.data.results || [];

      if (format === 'csv') {
        return MarketplaceServices.convertToCSV(data);
      }

      return data;
    } catch (error) {
      console.error('Erro ao exportar publicações:', error);
      throw error;
    }
  }

  // Converter dados para CSV
  static convertToCSV(data) {
    if (!data || data.length === 0) {
      return '';
    }

    const headers = [
      'ID',
      'Nome do Agente',
      'Área do Agente',
      'ID do Marketplace',
      'URL do Marketplace',
      'Status',
      'Preço (R$)',
      'Downloads',
      'Visualizações',
      'Avaliação',
      'Publicado em',
      'Criado em',
      'Atualizado em'
    ];

    const rows = data.map(pub => [
      pub.id,
      pub.agent_name || '',
      pub.agent_area || '',
      pub.marketplace_product_id || '',
      pub.PUBLICATIONS_URL || '',
      pub.status_display || pub.status,
      pub.price ? `R$ ${pub.price}` : 'Gratuito',
      pub.download_count || 0,
      pub.view_count || 0,
      pub.rating ? `${pub.rating}/5 (${pub.rating_count || 0} avaliações)` : 'Sem avaliações',
      pub.published_at ? new Date(pub.published_at).toLocaleDateString('pt-BR') : '',
      new Date(pub.created_at).toLocaleDateString('pt-BR'),
      new Date(pub.updated_at).toLocaleDateString('pt-BR')
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    return csvContent;
  }

  // Download do arquivo CSV
  static downloadCSV(csvContent, filename = 'marketplace-publicacoes.csv') {
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  // =============================
  // UPLOAD HELPERS
  // =============================

  // Converter arquivo para base64 (backup - API usa multipart/form-data)
  static fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  // Preparar arquivo para upload (validação + conversão)
  static async prepareFileForUpload(file, maxSizeMB = 3) {
    const validation = MarketplaceServices.validateImageFile(file, maxSizeMB);
    
    if (!validation.isValid) {
      throw new Error(`Arquivo inválido: ${validation.errors.join(', ')}`);
    }

    return {
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified
    };
  }

  // Preparar múltiplos arquivos para galeria
  static async prepareGalleryFiles(files, maxFiles = 10, maxSizeMB = 3) {
    if (!files || !Array.isArray(files)) {
      return [];
    }

    const validation = MarketplaceServices.validateGalleryFiles(files, maxFiles, maxSizeMB);
    
    if (!validation.isValid) {
      throw new Error(`Galeria inválida: ${validation.errors.join(', ')}`);
    }

    return Promise.all(
      files.map(file => MarketplaceServices.prepareFileForUpload(file, maxSizeMB))
    );
  }

  // Validar formato de imagem conforme API (PNG, JPG, JPEG, WEBP - máximo 3MB)
  static validateImageFile(file, maxSizeMB = 3) {
    const errors = [];
    
    if (!file) {
      errors.push("Arquivo é obrigatório");
      return { isValid: false, errors };
    }

    // Tipos aceitos pela API: PNG, JPG, JPEG, WEBP
    const validTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type.toLowerCase())) {
      errors.push("Formato de arquivo inválido. Apenas PNG, JPG, JPEG e WEBP são aceitos.");
    }

    // Tamanho máximo 3MB conforme documentação da API
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      errors.push(`Arquivo muito grande. Tamanho máximo: ${maxSizeMB}MB`);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Validar múltiplos arquivos da galeria (máximo 10 conforme API)
  static validateGalleryFiles(files, maxFiles = 10, maxSizeMB = 3) {
    const errors = [];
    
    if (!files || !Array.isArray(files)) {
      return { isValid: true, errors: [] }; // Galeria é opcional
    }

    if (files.length > maxFiles) {
      errors.push(`Máximo ${maxFiles} imagens permitidas na galeria.`);
    }

    files.forEach((file, index) => {
      const fileValidation = MarketplaceServices.validateImageFile(file, maxSizeMB);
      if (!fileValidation.isValid) {
        errors.push(`Imagem ${index + 1}: ${fileValidation.errors.join(', ')}`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Redimensionar imagem (usando canvas)
  static resizeImage(file, maxWidth = 1200, maxHeight = 400, quality = 0.8) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calcular novas dimensões mantendo proporção
        let { width, height } = img;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        canvas.width = width;
        canvas.height = height;

        // Desenhar imagem redimensionada
        ctx.drawImage(img, 0, 0, width, height);

        // Converter para base64
        const resizedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(resizedDataUrl);
      };

      img.src = URL.createObjectURL(file);
    });
  }

  // =============================
  // SELLER PROFILE SERVICES
  // =============================

  // Carregar dados do próprio perfil de vendedor
  static async loadSellerProfile() {
    try {
      const response = await api.get(
        `${MARKETPLACE_API_URL}seller-profiles/my_seller_data/`
      );
      return response;
    } catch (error) {
      console.error('Erro ao carregar perfil do vendedor:', error);
      throw error;
    }
  }

  // Atualizar perfil do vendedor existente
  static async updateSellerProfile(formData) {
    try {
      const response = await api.put(
        `${MARKETPLACE_API_URL}seller-profiles/update_profile/`,
        formData
      );
      return response;
    } catch (error) {
      console.error('Erro ao atualizar perfil do vendedor:', error);
      throw error;
    }
  }

  // Criar novo perfil de vendedor
  static async createSellerProfile(formData) {
    try {
      const response = await api.post(
        `${MARKETPLACE_API_URL}seller-profiles/`,
        formData
      );
      return response;
    } catch (error) {
      console.error('Erro ao criar perfil do vendedor:', error);
      throw error;
    }
  }
}

export default MarketplaceServices;